import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Play, CheckCircle2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Section, ExportButton, fmtTime, defaultRange } from "@/components/monitor/shared";

const TESTS = [
  ["load", "Sunucu yük testi"], ["concurrency", "Eş zamanlı kullanıcı testi"], ["large_list", "Büyük portföy listesi"], ["db", "Veritabanı sorgu süreleri"],
  ["image", "Görsel optimizasyonu"], ["cdn", "CDN bağlantısı"], ["alarm", "Hata ve alarm sistemi"], ["backup", "Yedekleme / geri yükleme hızı"],
];
const LABEL = Object.fromEntries(TESTS);

function Metrics({ m }) {
  const flat = Object.entries(m || {}).filter(([, v]) => v !== null && typeof v !== "object");
  const nested = Object.entries(m || {}).filter(([, v]) => v && typeof v === "object" && !Array.isArray(v));
  return (
    <div className="text-xs text-slate-600 space-y-1">
      <div className="flex flex-wrap gap-x-4 gap-y-1">{flat.map(([k, v]) => <span key={k}><span className="text-slate-400">{k}:</span> {String(v)}</span>)}</div>
      {nested.map(([k, v]) => <div key={k}><span className="text-slate-400">{k}:</span> {Object.entries(v).map(([a, b]) => `${a}=${typeof b === "object" ? JSON.stringify(b) : b}`).join(" · ")}</div>)}
    </div>
  );
}

export default function TestsTab() {
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(null);
  const load = () => api.get("/admin/monitor/tests").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const run = async (k) => {
    setRunning(k);
    try {
      const r = await api.post(`/admin/monitor/tests/${k}`, null, { timeout: 150000 });
      r.data.ok ? toast.success(`${LABEL[k]}: başarılı`) : toast.warning(`${LABEL[k]}: sorun bulundu`);
      load();
    } catch { toast.error("Test çalıştırılamadı"); }
    setRunning(null);
  };
  return (
    <div className="space-y-5" data-testid="monitor-tests">
      <Section title="Test çalıştır" testId="tests-run">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
          {TESTS.map(([k, l]) => (
            <Button key={k} data-testid={`run-test-${k}`} variant="outline" disabled={!!running} onClick={() => run(k)} className="justify-start">
              <Play className={`w-4 h-4 mr-2 ${running === k ? "animate-pulse text-emerald-600" : ""}`} />{running === k ? "Çalışıyor..." : l}
            </Button>
          ))}
        </div>
        <p className="text-[11px] text-slate-500 mt-3">Testler canlı sunucuda sınırlı ve kısa süreli yük oluşturur; yoğun saatlerde çalıştırmayın.</p>
      </Section>
      <Section title="Test sonuçları" testId="tests-history" right={<ExportButton kind="tests" range={defaultRange(24 * 30)} />}>
        {items.length === 0 ? <div className="text-sm text-slate-400">Henüz test çalıştırılmadı</div> : (
          <div className="divide-y divide-slate-100">
            {items.map((t) => (
              <div key={t.id} data-testid={`test-result-${t.id}`} className="py-3">
                <div className="flex flex-wrap items-center gap-2">
                  {t.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-red-600" />}
                  <span className="font-semibold text-sm">{LABEL[t.kind]}</span>
                  <span className="text-xs text-slate-500">{fmtTime(t.ran_at)} · {t.by} · {t.duration_ms} ms</span>
                </div>
                <div className="mt-1.5 pl-6"><Metrics m={t.metrics} />{t.notes?.map((n) => <div key={n} className="text-[11px] text-amber-700 mt-1">{n}</div>)}</div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
